/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { ChangeEvent, HTMLAttributes, useRef, useState } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import { Check } from '@adobe-commerce/elsie/icons';
import { Icon } from '@adobe-commerce/elsie/components/Icon';
import '@adobe-commerce/elsie/components/Checkbox/Checkbox.css';
import { useEffect } from 'preact/hooks';

export interface CheckboxProps
  extends Omit<HTMLAttributes<HTMLInputElement>, 'size' | 'label'> {
  name: string;
  size?: 'medium' | 'large';
  disabled?: boolean;
  error?: boolean;
  label?: string | VNode | VNode[];
  description?: string | VNode | VNode[];
}

export const Checkbox: FunctionComponent<CheckboxProps> = ({
  name,
  value,
  size = 'medium',
  disabled = false,
  error = false,
  label = '',
  description = '',
  className,
  checked,
  ...props
}) => {
  const [isChecked, setIsChecked] = useState(
    checked === undefined ? false : checked
  );
  const ref = useRef<HTMLInputElement>(null);

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(e);
    setIsChecked(e.currentTarget.checked);
  };

  const handleOnKeyDown = (e: KeyboardEvent) => {
    if (e.key === ' ') {
      e.preventDefault();
      ref?.current?.click();
    }
  };

  useEffect(() => {
    if (typeof checked === 'boolean') {
      setIsChecked(checked);
    }
  }, [checked]);

  return (
    <label
      className={classes([
        'dropin-checkbox',
        ['dropin-checkbox--disabled', disabled],
      ])}
    >
      <input
        ref={ref}
        name={name}
        value={value}
        type="checkbox"
        disabled={disabled}
        className={classes([
          'dropin-checkbox__checkbox',
          ['dropin-checkbox__checkbox--error', error],
          className,
        ])}
        {...props}
        onChange={handleCheckboxChange}
        checked={isChecked}
      />

      <div className="dropin-checkbox__checkbox-icon">
        {/*Zero-width space character, used to align checkbox properly*/}
        {'\u200B'}
        <span
          aria-checked={isChecked ? 'true' : 'false'}
          aria-labelledby={`${name}-label`}
          aria-describedby={description ? `${name}-description` : undefined}
          className={classes([
            'dropin-checkbox__box',
            ['dropin-checkbox__box--error', error],
            ['dropin-checkbox__box--disabled', disabled],
          ])}
          role="checkbox"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={handleOnKeyDown}
        >
          <Icon
            className={classes(['dropin-checkbox__checkmark'])}
            source={Check}
            size="16"
            stroke="3"
          />
        </span>
      </div>

      <div
        id={`${name}-label`}
        className={classes([
          'dropin-checkbox__label',
          `dropin-checkbox__label--${size}`,
          ['dropin-checkbox__label--disabled', disabled],
        ])}
      >
        {label}
      </div>

      <div />
      {description && (
        <div
          id={`${name}-description`}
          role={'note'}
          className={classes([
            'dropin-checkbox__description',
            `dropin-checkbox__description--${size}`,
            ['dropin-checkbox__description--disabled', disabled],
          ])}
        >
          {description}
        </div>
      )}
    </label>
  );
};
